import React, { useState, useMemo, useEffect } from "react";
import { useCreateNotification, useGetAllDoctors } from "../apis/doctor";
import { useGetPatientDetails, useReferPatient } from "../apis/patients";
import { Button } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "../hooks/useAppSelector";
interface ReferralFormProps {
  appointmentId: string | undefined;
  patientId?: string;
}

const ReferralForm: React.FC<ReferralFormProps> = ({
  appointmentId,
  patientId,
}) => {
  const location = useLocation();
  const patientIdFromRoute = React.useMemo(
    () => location?.pathname?.split("/")?.[2] ?? "",
    [location?.pathname],
  );
  const [selectedSpecialty, setSelectedSpecialty] = useState("All");
  const [search, setSearch] = useState("");
  const [selectedDoctorId, setSelectedDoctorId] = useState("");

  const { data: patientResponse } = useGetPatientDetails({
    patientId: patientIdFromRoute,
  });

  // For tracking the current page number
  const [currentPage, setCurrentPage] = useState(1);

  // For tracking the user's search input
  const [searchTerm, setSearchTerm] = useState("");

  const { mutate: createNotification } = useCreateNotification();
  // ✅ Sync search input with the searchTerm used in query
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchTerm(search);
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [search]);

  const { doctors } = useGetAllDoctors({
    limit: 100,
    page: currentPage,
    specialty: selectedSpecialty,
    search: searchTerm,
  });

  const finalDoctors = doctors || [];

  const { mutate: referPatient, isPending: isSubmitting } = useReferPatient();

  const { doctorInfo } = useAppSelector((state) => state.auth);
  const userId = doctorInfo?._id;

  const referringDoctor = useMemo(() => {
    return doctors?.find((doc) => doc._id === userId);
  }, [doctors]);

  const referringDoctorName = referringDoctor
    ? `${referringDoctor.title || "Dr"} ${referringDoctor.name}`
    : "A doctor";

  const patientName = patientResponse?.data?.name || "a patient";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const finalPatientId = patientId || patientIdFromRoute;

    if (!selectedDoctorId || !finalPatientId || !appointmentId) return;

    referPatient(
      {
        patientId: String(finalPatientId),
        doctorId: selectedDoctorId,
      },
      {
        onSuccess: () => {
          createNotification({
            userId: selectedDoctorId,
            title: "Patient Referred",
            message: `${referringDoctorName} has referred ${patientName} (ID: ${patientIdFromRoute}) to you.`,
          });
        },
        onError: (err) => {
          console.error("Referral failed:", err);
        },
      },
    );
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-lg max-w-2xl mx-auto">
      <h2 className="text-xl font-bold mb-4">Refer Patient</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Specialty Filter */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Filter by Specialty:
          </label>
          <select
            value={selectedSpecialty}
            onChange={(e) => setSelectedSpecialty(e.target.value)}
            className="w-full border p-2 rounded"
          >
            <option value="All">All</option>
            <option value="Pediatrician">Pediatrician</option>
            <option value="Obstetrician and Gynecologist">
              Obstetrician and Gynecologist
            </option>
            <option value="Neurologist">Neurologist</option>
            <option value="Ophthalmologist">Ophthalmologist</option>
            <option value="Otolaryngologist">Otolaryngologist</option>
            <option value="Dermatologist">Dermatologist</option>
            <option value="Cardiologist">Cardiologist</option>
            <option value="Chest Physician">Chest Physician</option>
            <option value="Gastroenterologist">Gastroenterologist</option>
            <option value="Urologist">Urologist</option>
            <option value="Orthopedic Surgeon">Orthopedic Surgeon</option>
            <option value="Psychiatrist">Psychiatrist</option>
            <option value="Dentist">Dentist</option>
            <option value="Obstetrician">Obstetrician</option>
            <option value="Gynecologist">Gynecologist</option>
            <option value="Family Medicine Physician">
              Family Medicine Physician
            </option>
            <option value="Generalist">Generalist</option>
            <option value="Nutritionist">Nutritionist</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Search by Name */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Search by Name:
          </label>
          <input
            type="text"
            placeholder="Enter doctor name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border p-2 rounded"
          />
        </div>

        {/* Doctor Dropdown */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Select Doctor:
          </label>

          <select
            value={selectedDoctorId}
            onChange={(e) => setSelectedDoctorId(e.target.value)}
            className="w-full border p-2 rounded"
          >
            <option value="">-- Select Doctor --</option>
            {finalDoctors.map((doc: any) => (
              <option key={doc._id} value={doc._id}>
                {doc.name} (
                {Array.isArray(doc.specialty)
                  ? doc.specialty.join(", ")
                  : doc.specialty}
                )
              </option>
            ))}
          </select>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline">
            Cancel
          </Button>
          <Button type="submit" disabled={!selectedDoctorId || isSubmitting}>
            {isSubmitting ? "Referring..." : "Refer Patient"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ReferralForm;
